import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading, children, disabled, ...props }, ref) => {
    const baseStyles =
      'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg'

    const variants = {
      primary:
        'bg-primary text-white hover:bg-primary-hover active:scale-[0.98] shadow-md hover:shadow-lg',
      secondary:
        'bg-secondary text-white hover:opacity-90 active:scale-[0.98] shadow-md hover:shadow-lg',
      outline:
        'border-2 border-primary text-primary hover:bg-primary hover:text-white active:scale-[0.98]',
      ghost:
        'text-foreground hover:bg-background-tertiary active:scale-[0.98]',
    }

    const sizes = {
      // Mobile-first: ensure minimum 44px touch target (increased padding)
      sm: 'text-sm px-4 py-2.5 min-h-[44px] gap-1.5',
      md: 'text-base px-6 py-3 min-h-[48px] gap-2',
      lg: 'text-lg px-8 py-4 min-h-[52px] gap-2.5',
    }

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }
